import type { PerformerStatus } from '@stage/contracts';
import type { AdminInteraction } from '../api/client.js';

/**
 * A single interaction card in the Performance Console queue (§24.3). Shows only what a performer
 * needs to decide. One-tap actions are grouped; per §24.3 "hide" must be visually distinct from
 * "perform", so it is separated into its own destructive group.
 */
export interface QueueCardProps {
  interaction: AdminInteraction;
  pending: boolean;
  onAct: (next: PerformerStatus) => void;
}

const PRIMARY_ACTIONS: ReadonlyArray<{ status: PerformerStatus; label: string }> = [
  { status: 'shortlisted', label: 'Shortlist' },
  { status: 'accepted', label: 'Accept' },
  { status: 'queued', label: 'Queue' },
  { status: 'performed', label: 'Performed' },
];

function title(interaction: AdminInteraction): string {
  if (interaction.interactionType === 'song_request') {
    return interaction.songId;
  }
  return interaction.message;
}

function detail(interaction: AdminInteraction): string | null {
  if (interaction.interactionType === 'song_request') return interaction.note;
  return interaction.category;
}

export function QueueCard({ interaction, pending, onAct }: QueueCardProps) {
  const nickname =
    interaction.interactionType === 'song_request' || interaction.interactionType === 'freeform'
      ? interaction.nickname
      : null;
  const detailText = detail(interaction);
  const kindLabel = interaction.interactionType === 'song_request' ? 'Song request' : 'Suggestion';

  return (
    <li className={`qcard qcard--${interaction.performerStatus}`}>
      <div className="qcard__head">
        <span className="badge small">{kindLabel}</span>
        <span className="qcard__status">{interaction.performerStatus}</span>
        {pending && (
          <span className="qcard__pending" title="Sending…" aria-live="polite">
            ⧗ pending
          </span>
        )}
      </div>
      <p className="qcard__title">{title(interaction)}</p>
      {detailText && <p className="qcard__detail">{detailText}</p>}
      {nickname && <p className="qcard__nick muted">— {nickname}</p>}

      <div className="qcard__actions">
        <div className="qcard__group">
          {PRIMARY_ACTIONS.map((a) => (
            <button
              key={a.status}
              type="button"
              className="qbtn"
              disabled={pending || interaction.performerStatus === a.status}
              onClick={() => onAct(a.status)}
            >
              {a.label}
            </button>
          ))}
        </div>
        {/* Destructive group kept apart so "hide" is never mistaken for "perform" (§24.3). */}
        <div className="qcard__group qcard__group--danger">
          <button
            type="button"
            className="qbtn qbtn--hide"
            disabled={pending || interaction.performerStatus === 'hidden'}
            onClick={() => onAct('hidden')}
          >
            Hide
          </button>
        </div>
      </div>
    </li>
  );
}
